import {
  Paper,
  Stack,
  Group,
  Text,
  Title,
  Button,
  Badge,
  Skeleton,
  Box,
} from '@mantine/core';
import {
  IconBook,
  IconAbc,
  IconClock,
  IconFlame,
  IconSparkles,
  IconStar,
  IconMessageCircle,
  IconChecklist,
  IconEye,
  IconPlus,
  IconWand,
  IconCoffeeOff,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeeklyRecap } from '../hooks/useWeeklyRecap';
import type {
  RecapItem,
  StatItem,
  HighlightItem,
  PromptItem,
  CTAItem,
  QuietItem,
  ThemeSuggestion,
} from '../types/recap';
import classes from './WeeklyRecapCard.module.css';

interface Props {
  childId: string;
  childDisplayName: string;
  onSelectTheme?: (themeId: string, childId: string) => void;
}

export function WeeklyRecapCard({ childId, childDisplayName, onSelectTheme }: Props) {
  const { data, isLoading, isError } = useWeeklyRecap(childId);

  if (isLoading) {
    return <Skeleton height={260} radius="md" />;
  }
  if (isError || !data) {
    return null;
  }

  const headline =
    data.variant === 'first_days'
      ? `${childDisplayName}'s first days`
      : `This week with ${childDisplayName}`;

  const stats = data.items.filter((i): i is StatItem => i.type === 'stat');
  const others = data.items.filter((i) => i.type !== 'stat');

  const handleTheme = onSelectTheme
    ? (themeId: string) => onSelectTheme(themeId, childId)
    : undefined;

  return (
    <Paper p="lg" radius="md" shadow="sm" withBorder className={classes.card}>
      <Stack gap="md">
        <Title order={4}>{headline}</Title>

        {stats.length > 0 && <StatList items={stats} />}

        {others.map((item, idx) => (
          <ItemRenderer
            key={`${item.type}-${idx}`}
            item={item}
            onSelectTheme={handleTheme}
          />
        ))}
      </Stack>
    </Paper>
  );
}

// -- renderers --------------------------------------------------------------

const STAT_META: Record<string, { icon: ReactNode; color: string }> = {
  'Stories read': { icon: <IconBook size={18} />, color: 'teal' },
  'Words learned': { icon: <IconAbc size={18} />, color: 'grape' },
  'Minutes engaged': { icon: <IconClock size={18} />, color: 'blue' },
};

function StatList({ items }: { items: StatItem[] }) {
  return (
    <Stack gap="xs">
      {items.map((s) => {
        const meta = STAT_META[s.label] ?? { icon: <IconStar size={18} />, color: 'teal' };
        return (
          <Group key={s.label} gap="sm" wrap="nowrap" align="center">
            <Box c={`${meta.color}.6`}>{meta.icon}</Box>
            <Text size="sm" c="dimmed" style={{ flex: 1 }}>
              {s.label}
            </Text>
            <Group gap={6} align="baseline">
              <Text fw={600} c={s.value === 0 ? 'dimmed' : undefined}>
                {s.value}
              </Text>
              {typeof s.delta === 'number' && s.delta !== 0 && (
                <Text size="xs" c={s.delta > 0 ? 'teal.7' : 'gray.6'} fw={500}>
                  {s.delta > 0 ? '+' : ''}
                  {s.delta}
                </Text>
              )}
            </Group>
          </Group>
        );
      })}
    </Stack>
  );
}

interface ItemProps {
  item: RecapItem;
  onSelectTheme?: (themeId: string) => void;
}

function ItemRenderer({ item, onSelectTheme }: ItemProps) {
  switch (item.type) {
    case 'highlight':
      return <HighlightRow item={item} />;
    case 'prompt':
      return <PromptRow item={item} />;
    case 'cta':
      return <CTARow item={item} onSelectTheme={onSelectTheme} />;
    case 'quiet':
      return <QuietRow item={item} onSelectTheme={onSelectTheme} />;
    case 'stat':
      return null;
  }
}

function highlightIcon(detector: string): ReactNode {
  switch (detector) {
    case 'streak':
      return <IconFlame size={18} />;
    case 'theme_exploration':
      return <IconSparkles size={18} />;
    default:
      return <IconStar size={18} />;
  }
}

function HighlightRow({ item }: { item: HighlightItem }) {
  return (
    <Group gap="sm" wrap="nowrap">
      <Box c="teal.6">{highlightIcon(item.detector)}</Box>
      <Text fw={500} size="sm">
        {item.text}
      </Text>
    </Group>
  );
}

function PromptRow({ item }: { item: PromptItem }) {
  return (
    <Paper p="sm" radius="sm" className={classes.promptBlock}>
      <Stack gap={4}>
        <Group gap="xs" wrap="nowrap">
          <Box c="teal.6">
            <IconMessageCircle size={16} />
          </Box>
          <Text size="xs" c="dimmed" fw={600} style={{ flex: 1 }}>
            Ask about “{item.story_title}”
          </Text>
        </Group>
        <Text fs="italic" size="sm">
          {item.question}
        </Text>
      </Stack>
    </Paper>
  );
}

function ctaIcon(kind: CTAItem['kind']): ReactNode {
  switch (kind) {
    case 'review_answers':
      return <IconChecklist size={14} />;
    case 'view_assignment':
      return <IconEye size={14} />;
    case 'assign_next':
      return <IconPlus size={14} />;
    case 'try_new_theme':
      return <IconWand size={14} />;
  }
}

function CTARow({
  item,
  onSelectTheme,
}: {
  item: CTAItem;
  onSelectTheme?: (themeId: string) => void;
}) {
  const navigate = useNavigate();
  const buttonColor =
    item.kind === 'view_assignment' ? 'orange' : 'teal';

  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="nowrap">
        <Box c={`${buttonColor}.6`}>{ctaIcon(item.kind)}</Box>
        <Text size="sm" style={{ flex: 1 }}>
          {item.label}
        </Text>
        {item.target_url && (
          <Button
            variant="light"
            color={buttonColor}
            size="xs"
            onClick={() => item.target_url && navigate(item.target_url)}
          >
            Open
          </Button>
        )}
      </Group>
      {item.suggestions && item.suggestions.length > 0 && (
        <Group gap="xs" wrap="wrap">
          {item.suggestions.map((s) => (
            <ThemePill
              key={s.theme_id}
              suggestion={s}
              onClick={onSelectTheme}
            />
          ))}
        </Group>
      )}
    </Stack>
  );
}

function QuietRow({
  item,
  onSelectTheme,
}: {
  item: QuietItem;
  onSelectTheme?: (themeId: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <Paper p="md" radius="md" className={classes.quietBlock}>
      <Stack gap="xs">
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Box c="teal.7" mt={2}>
            <IconCoffeeOff size={18} />
          </Box>
          <Text size="sm" fw={500} c="dimmed" style={{ flex: 1 }}>
            {item.message}
          </Text>
        </Group>
        {item.suggestions && item.suggestions.length > 0 && (
          <Group gap="xs" wrap="wrap">
            {item.suggestions.map((s) => (
              <ThemePill
                key={s.theme_id}
                suggestion={s}
                onClick={onSelectTheme}
              />
            ))}
          </Group>
        )}
        {item.target_url && (
          <Group>
            <Button
              variant="light"
              color="teal"
              size="xs"
              leftSection={<IconEye size={14} />}
              onClick={() => item.target_url && navigate(item.target_url)}
            >
              View assignments
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

function ThemePill({
  suggestion,
  onClick,
}: {
  suggestion: ThemeSuggestion;
  onClick?: (themeId: string) => void;
}) {
  const label = `${suggestion.emoji ? suggestion.emoji + ' ' : ''}${suggestion.label}`;

  if (!onClick) {
    return (
      <Badge variant="light" color="teal" size="lg" radius="sm">
        {label}
      </Badge>
    );
  }

  return (
    <Button
      variant="light"
      color="teal"
      size="xs"
      radius="sm"
      onClick={() => onClick(suggestion.theme_id)}
      title={`Assign a story about ${suggestion.label}`}
    >
      {label}
    </Button>
  );
}
